import { Popover } from 'antd';
import {
  DeliveredEmailIcon,
  SmsIcon,
  WarningIcon,
  VisibilityoffNotificationIcon,
  EmailOpenIcon,
  ClickedIcon,
  CalendarClockIcon,
} from 'src/icons';
import moment from 'moment';
import { ServiceRequest } from 'src/models';
import { capitalizeFirstLetter } from 'src/modules/appointments/utils/constants';
import { Table, Modal, Button } from '@insight-health-ai/roseate/components';

interface NotificationData {
  schedulded_notifications: Array<{
    muted: boolean;
    notification_data: string;
    schedule: string;
    to_send_email: string;
    to_send_sms: string;
  }>;
  sent_notifications: Array<{
    delivery_method: 'sms' | 'email';
    delivery_status: string;
    notification_date: string;
    sent_by: string;
    sent_to: string;
  }>;
}

interface NotificationHistoryModalProps {
  open: boolean;
  onClose: () => void;
  record: ServiceRequest | null;
  notificationData: NotificationData | null;
  isLoading: boolean;
}

export const NotificationHistoryModal = ({
  open,
  onClose,
  notificationData,
  isLoading,
}: NotificationHistoryModalProps) => {
  const notificationSentColumns = [
    {
      title: 'Date',
      dataIndex: 'notification_date',
      key: 'date',
      render: (date: string) => {
        try {
          const utcDate = moment.utc(date, 'MMMM Do, YYYY h:mm A');
          const userTimezone = moment.tz.guess();
          const localDate = utcDate.tz(userTimezone);
          const formattedDate = localDate.format('MMM Do, YYYY h:mm A');

          return formattedDate;
        } catch {
          return date;
        }
      },
    },
    {
      title: 'Sent to',
      dataIndex: 'sent_to',
      key: 'sent_to',
      render: (text: string, record: any) => (
        <div className="flex items-center gap-2">
          {record.delivery_method === 'email' ? (
            <DeliveredEmailIcon
              id="email"
              className="color-89CFF0"
              isClickable={false}
              onClick={() => {}}
            />
          ) : (
            <SmsIcon
              id="sms"
              className="color-8BCB53"
              isClickable={false}
              onClick={() => {}}
            />
          )}
          <span className="text-sm">{text}</span>
        </div>
      ),
    },
    {
      title: 'Delivery status',
      dataIndex: 'delivery_status',
      key: 'status',
      render: (text: string) => {
        let deliveryStatusIcon;
        let statusTooltipText;
        switch (text?.toLowerCase()) {
          case 'clicked':
          case 'click':
            deliveryStatusIcon = (
              <ClickedIcon className="" onClick={() => {}} />
            );
            statusTooltipText = 'Clicked';
            break;
          case 'opened':
          case 'open':
            deliveryStatusIcon = (
              <EmailOpenIcon className="" onClick={() => {}} />
            );
            statusTooltipText = 'Opened';
            break;
          case 'delivery':
          case 'delivered':
            statusTooltipText = 'Delivered';
            deliveryStatusIcon = (
              <VisibilityoffNotificationIcon
                id=""
                className=""
                onClick={() => {}}
              />
            );
            break;
          case 'sent':
          case 'send':
            statusTooltipText = 'Sent';
            deliveryStatusIcon = (
              <VisibilityoffNotificationIcon
                id=""
                className=""
                onClick={() => {}}
              />
            );
            break;
          case 'failed':
          case 'complaint':
            statusTooltipText = 'Failed';
            deliveryStatusIcon = (
              <WarningIcon id="" className="" onClick={() => {}} />
            );
            break;
          case 'bounced':
            statusTooltipText = 'Bounced';
            deliveryStatusIcon = (
              <WarningIcon id="" className="" onClick={() => {}} />
            );
            break;
          case 'Bounce':
            statusTooltipText = 'Bounced';
            deliveryStatusIcon = (
              <WarningIcon id="" className="" onClick={() => {}} />
            );
            break;
          case 'rejected':
          case 'reject':
            statusTooltipText = 'Rejected';
            deliveryStatusIcon = (
              <WarningIcon id="" className="" onClick={() => {}} />
            );
            break;
          case 'exception':
            statusTooltipText = 'Exception';
            deliveryStatusIcon = (
              <WarningIcon id="" className="" onClick={() => {}} />
            );
            break;
          case 'phone number is invalid':
          case 'email is invalid':
            statusTooltipText = capitalizeFirstLetter(text);
            deliveryStatusIcon = (
              <WarningIcon id="" className="" onClick={() => {}} />
            );
            break;
          default:
            deliveryStatusIcon = capitalizeFirstLetter(text);
        }
        return (
          <div className="ml-4">
            <Popover placement="topLeft" content={statusTooltipText}>
              <div>{deliveryStatusIcon}</div>
            </Popover>
          </div>
        );
      },
    },
    {
      title: 'Sent by',
      dataIndex: 'sent_by',
      key: 'sent_by',
      render: (text: string) => (
        <span className="capitalize">
          {text === 'scheduled'
            ? 'Automation'
            : text === 'immediate'
              ? 'Immediate'
              : text}
        </span>
      ),
    },
  ];

  const notificationScheduledColumns = [
    {
      title: 'Notification date',
      dataIndex: 'notification_data',
      key: 'date',
      render: (date: string) => {
        try {
          const utcDate = moment.utc(date, 'MMMM Do, YYYY h:mm A');
          const userTimezone = moment.tz.guess();
          const localDate = utcDate.tz(userTimezone);
          const formattedDate = localDate.format('MMM Do, YYYY h:mm A');

          return (
            <div className="flex items-center gap-2">
              <CalendarClockIcon
                className="text-green-500"
                onClick={() => {}}
              />
              <span>{formattedDate}</span>
            </div>
          );
        } catch {
          return (
            <div className="flex items-center gap-2">
              <CalendarClockIcon
                className="text-green-500"
                onClick={() => {}}
              />
              <span>{date}</span>
            </div>
          );
        }
      },
    },
    {
      title: 'Send email to',
      dataIndex: 'to_send_email',
      key: 'email',
      render: (text: string) => (
        <div className="flex items-center gap-2">
          <DeliveredEmailIcon
            id="email"
            className="color-89CFF0"
            isClickable={false}
            onClick={() => {}}
          />
          <span className="text-sm">{text}</span>
        </div>
      ),
    },
    {
      title: 'Send SMS to',
      dataIndex: 'to_send_sms',
      key: 'sms',
      render: (text: string) => (
        <div className="flex items-center gap-2">
          <SmsIcon
            id="sms"
            className="color-8BCB53"
            isClickable={false}
            onClick={() => {}}
          />
          <span className="text-sm">{text}</span>
        </div>
      ),
    },
    {
      title: 'Schedule',
      dataIndex: 'schedule',
      key: 'schedule',
      render: (text: string) => <span className="text-gray-700">{text}</span>,
    },
  ];

  return (
    <Modal
      width={1000}
      open={open}
      onClose={onClose}
      centered
      title={<span className="text-lg font-bold">Notification history</span>}
      footer={
        <div className="flex justify-end gap-2 mt-8">
          <Button variant="outlined" onClick={onClose} appearance="secondary">
            Close
          </Button>
        </div>
      }
    >
      <div className="mt-6">
        <h3 className="text-base mb-3 text-gray-800 font-bold">
          Notifications sent
        </h3>
        <Table
          data={notificationData?.sent_notifications || []}
          columns={notificationSentColumns}
          pagination={{
            pageSize: 3,
            showSizeChanger: false,
            showQuickJumper: false,
            size: 'small',
            position: ['bottomRight'],
          }}
          loading={isLoading}
        />
      </div>

      <div className="mt-2">
        <h3 className="text-base mb-3 text-gray-800 font-bold">
          Notifications scheduled
        </h3>
        <Table
          data={notificationData?.schedulded_notifications || []}
          columns={notificationScheduledColumns}
          pagination={{
            pageSize: 3,
            showSizeChanger: false,
            showQuickJumper: false,
            size: 'small',
            position: ['bottomRight'],
          }}
          loading={isLoading}
        />
      </div>
    </Modal>
  );
};
